import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BedDouble, CarTaxiFront, Palmtree, Plane, Search } from "lucide-react";
import { Field, PrimaryBtn, inputCls, Opt } from "@/components/Common";
import { http, today } from "@/lib/api";

const TABS = [
  ["flights", "Flights", Plane],
  ["hotels", "Hotels", BedDouble],
  ["cabs", "Taxi", CarTaxiFront],
  ["packages", "Holidays", Palmtree],
];

export const useAirports = () => useQuery({ queryKey: ["airports"], queryFn: () => http.get("/airports").then((r) => r.data) });
export const useCities = () => useQuery({ queryKey: ["cities"], queryFn: () => http.get("/hotels/cities").then((r) => r.data) });

export const AirportSelect = ({ value, onChange, testid }) => {
  const { data = [] } = useAirports();
  return (
    <select className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {["domestic", "international"].map((g) => (
        <optgroup key={g} label={g === "domestic" ? "India" : "International"}>
          {data.filter((a) => a.region === g).map((a) => <Opt key={a.code} v={a.code} l={`${a.city} (${a.code})`} />)}
        </optgroup>
      ))}
    </select>
  );
};

export const HeroSearch = () => {
  const navigate = useNavigate();
  const { data: cities = [] } = useCities();
  const [tab, setTab] = useState("flights");
  const [f, setF] = useState({ from: "DEL", to: "GOI", date: today(7), cabDate: today(3), city: "Goa", checkin: today(7), checkout: today(9), trip: "airport", pickup: "", region: "domestic" });
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });

  const go = () => {
    const q = {
      flights: `/flights?from=${f.from}&to=${f.to}&date=${f.date}`,
      hotels: `/hotels?city=${encodeURIComponent(f.city)}&checkin=${f.checkin}&checkout=${f.checkout}`,
      cabs: `/cabs?trip=${f.trip}&pickup=${encodeURIComponent(f.pickup)}&date=${f.cabDate}`,
      packages: `/packages?region=${f.region}`,
    }[tab];
    navigate(q);
  };

  return (
    <div className="rounded-2xl bg-white/95 p-2 shadow-2xl shadow-black/30 backdrop-blur" data-testid="hero-search">
      <div className="flex flex-wrap gap-1 p-1">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`hero-tab-${k}`}
            className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-[#0D1B2A] text-white" : "text-slate-600 hover:bg-[#F4F0EA]"}`}>
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </div>
      <div className="grid items-end gap-3 p-4 sm:grid-cols-2 lg:grid-cols-[repeat(3,minmax(0,1fr))_auto]">
        {tab === "flights" && <>
          <Field label="From"><AirportSelect value={f.from} onChange={set("from")} testid="hero-from-select" /></Field>
          <Field label="To"><AirportSelect value={f.to} onChange={set("to")} testid="hero-to-select" /></Field>
          <Field label="Departure"><input type="date" min={today()} className={inputCls} value={f.date} onChange={set("date")} data-testid="hero-date-input" /></Field>
        </>}
        {tab === "hotels" && <>
          <Field label="City">
            <select className={inputCls} value={f.city} onChange={set("city")} data-testid="hero-city-select">
              {cities.map((c) => <Opt key={c} v={c} l={c} />)}
            </select>
          </Field>
          <Field label="Check-in"><input type="date" min={today()} className={inputCls} value={f.checkin} onChange={set("checkin")} data-testid="hero-checkin-input" /></Field>
          <Field label="Check-out"><input type="date" min={f.checkin} className={inputCls} value={f.checkout} onChange={set("checkout")} data-testid="hero-checkout-input" /></Field>
        </>}
        {tab === "cabs" && <>
          <Field label="Trip type">
            <select className={inputCls} value={f.trip} onChange={set("trip")} data-testid="hero-trip-select">
              <option value="airport">Airport transfer</option><option value="local">Local rental (8h/80km)</option><option value="outstation">Outstation</option>
            </select>
          </Field>
          <Field label="Pickup city / address"><input className={inputCls} value={f.pickup} onChange={set("pickup")} placeholder="e.g. Connaught Place, Delhi" data-testid="hero-pickup-input" /></Field>
          <Field label="Pickup date"><input type="date" min={today()} className={inputCls} value={f.cabDate} onChange={set("cabDate")} data-testid="hero-cab-date-input" /></Field>
        </>}
        {tab === "packages" && <>
          <Field label="Where to?">
            <select className={inputCls} value={f.region} onChange={set("region")} data-testid="hero-region-select">
              <option value="domestic">Within India</option><option value="international">International</option>
            </select>
          </Field>
          <div className="hidden sm:block" /><div className="hidden lg:block" />
        </>}
        <PrimaryBtn onClick={go} className="h-11 sm:col-span-2 lg:col-span-1" data-testid="hero-search-btn"><Search className="h-4 w-4" />Search</PrimaryBtn>
      </div>
    </div>
  );
};
